import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { useGetAccountLifecycleCompetitorsQuery, useGetAccountLifecycleReasonsQuery } from 'api/account-lifecycle/accountLifecycle'
import { useReactivateAccountWithCaptureMutation } from 'api/accounts/reactivateAccountWithCapture'
import LifecycleCaptureFields from 'components/common/account-lifecycle/LifecycleCaptureFields'
import useLifecycleReasonOptions from 'components/common/account-lifecycle/useLifecycleReasonOptions'
import { HHAlert } from 'components/common/HHAlert'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { pushReactivationStartedToast } from 'components/notifications/notification-center/notificationCenterToast'
import { NOTIFICATION_PREF_KEY } from 'components/notifications/notification-center/notificationCenterViews'
import { useCanReceiveNotification } from 'components/notifications/notification-center/useIsNotificationCenterAvailable'
import { selectIsAccountLifecycleCaptureEnabled } from 'data/tenant/tenantSettingsSelectors'
import T from 'T'
import { createDateFromString, formatDateToBEFormatDateFns, formatDateToFEFormatDateFns, isNotInvalidDate } from 'utils/date'
import { handleErrorWithFetchError } from 'utils/error'

import ReactivationSelectionList from './ReactivationSelectionList'
import ReactivationSelectionListSkeleton from './ReactivationSelectionListSkeleton'
import ReactivationStartDates from './ReactivationStartDates'
import ReactivationWhen, { isValidScheduleDate } from './ReactivationWhen'
import useReactivationSelection from './useReactivationSelection'

import type { ReactivationTiming } from './ReactivationWhen'
import type { LifecycleCaptureValues } from 'components/common/account-lifecycle/LifecycleCaptureFields'
import type { AccountLifecycleCompetitorApi, AccountLifecycleReasonApi } from 'types/api/account-lifecycle/accountLifecycle'
import type { PendingScheduleApi, ReactivationStartDatesApi, ReactivationStatusApi } from 'types/api/account/accountReactivation'

type ReactivateAccountDialogProps = {
  isOpen: boolean
  accountId: string
  accountName: string
  accountNumber?: string
  pendingSchedule?: PendingScheduleApi | null
  onClose: () => void
  onReactivated: (status: ReactivationStatusApi) => void
}

type ServerError = { data?: { exceptionType?: string; message?: string } }

const INITIAL_VALUES: LifecycleCaptureValues = { reasonId: '', competitorId: '', notes: '' }
const NO_REASONS: AccountLifecycleReasonApi[] = []
const NO_COMPETITORS: AccountLifecycleCompetitorApi[] = []
const DEFAULT_START_DATES: ReactivationStartDatesApi = 'TODAY'

const countLabel = (count: number, one: string, many: string) => `${count} ${(count === 1 ? one : many).toLowerCase()}`

const ReactivateAccountDialog = ({
  isOpen,
  accountId,
  accountName,
  accountNumber,
  pendingSchedule,
  onClose,
  onReactivated,
}: ReactivateAccountDialogProps) => {
  const schedule = pendingSchedule?.type === 'REACTIVATION' ? pendingSchedule : undefined
  const isCaptureEnabled = useSelector(selectIsAccountLifecycleCaptureEnabled)
  const canReceiveReactivationNotification = useCanReceiveNotification(NOTIFICATION_PREF_KEY.ACCOUNT_REACTIVATION)
  const skip = !isOpen || !isCaptureEnabled
  const { data: reasonData, isError: hasReasonsError } = useGetAccountLifecycleReasonsQuery({ type: 'WON' }, { skip })
  const { data: competitorData, isError: hasCompetitorsError } = useGetAccountLifecycleCompetitorsQuery(undefined, { skip })
  const [reactivateAccount, { isLoading }] = useReactivateAccountWithCaptureMutation()
  const [startDates, setStartDates] = useState<ReactivationStartDatesApi>(DEFAULT_START_DATES)
  const [timing, setTiming] = useState<ReactivationTiming>('NOW')
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(null)
  const [effectiveDateError, setEffectiveDateError] = useState<string>()
  const isScheduling = timing === 'SCHEDULED'
  const shownDate = isScheduling && effectiveDate && isNotInvalidDate(effectiveDate) ? effectiveDate : null
  const scheduledDate = shownDate && isValidScheduleDate(shownDate) ? shownDate : null
  const formattedEffectiveDate = shownDate ? formatDateToFEFormatDateFns(shownDate) : undefined
  // A saved schedule reopened on or after its date shows that date, which can no longer be picked.
  const dateError = effectiveDateError ?? (shownDate && !scheduledDate ? T.REACTIVATION_DATE_AFTER_TODAY : undefined)
  const {
    groups,
    selection,
    hasSelection,
    isSelectionReady,
    isSelectionLoading,
    hasSelectionError,
    handleToggleLocation,
    handleToggleService,
    handleToggleAll,
  } = useReactivationSelection({ accountId, isOpen, saved: schedule })
  const {
    control,
    handleSubmit,
    reset,
    resetField,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<LifecycleCaptureValues>({ defaultValues: INITIAL_VALUES })
  const wonReasonId = useWatch({ control, name: 'reasonId' })
  const wonCompetitorId = useWatch({ control, name: 'competitorId' })

  const reasons = reasonData?.reasons ?? NO_REASONS
  // With capture on, required once there is a won reason to pick; a list that failed to load leaves it to the server.
  const isReasonRequired = isCaptureEnabled && !hasReasonsError && (!reasonData || reasons.length > 0)
  const { reasonOptions, competitorOptions, showCompetitor } = useLifecycleReasonOptions({
    reasons,
    competitors: competitorData?.competitors ?? NO_COMPETITORS,
    isReasonRequired,
    selectedReasonId: wonReasonId,
  })
  const canConfirm =
    isSelectionReady &&
    (!isScheduling || Boolean(scheduledDate)) &&
    (!isReasonRequired || Boolean(wonReasonId)) &&
    (!showCompetitor || Boolean(wonCompetitorId)) &&
    !errors.reasonId &&
    !errors.competitorId &&
    !effectiveDateError
  const reasonError = errors.reasonId?.message ?? (hasReasonsError ? T.WON_REASONS_LOAD_ERROR : undefined)
  const hasNoReasonToPick = Boolean(reasonData) && reasons.length === 0
  const reasonHelperText = reasonError ?? (hasNoReasonToPick ? T.NO_WON_REASONS_SET_UP : T.WON_REASON_LOGGED_FOR_REPORTING)
  const competitorError = errors.competitorId?.message ?? (hasCompetitorsError ? T.COMPETITORS_LOAD_ERROR : undefined)
  const todayBillingNotice = formattedEffectiveDate
    ? T.REACTIVATION_NOTICE_BILLING_FROM.replace('%s', formattedEffectiveDate)
    : T.REACTIVATION_NOTICE_BILLING
  const selectionSummary = !hasSelection
    ? T.REACTIVATION_ACCOUNT_ONLY
    : `${countLabel(selection.locationIds.size, T.LOCATION, T.LOCATIONS)} · ${countLabel(
        selection.serviceIds.size,
        T.SERVICE,
        T.SERVICES
      )} ${T.WILL_BE_REACTIVATED}`

  useEffect(() => {
    if (isOpen) return
    reset(INITIAL_VALUES)
    setStartDates(DEFAULT_START_DATES)
    setTiming('NOW')
    setEffectiveDate(null)
    setEffectiveDateError(undefined)
  }, [isOpen, reset])

  // Reopened on or after its date, the schedule cannot be saved until the date changes, so start the user there.
  const savedDate = schedule?.effectiveDate
  const dateFieldRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const savedDay = savedDate ? createDateFromString(savedDate) : null
    if (!isOpen || !isScheduling || !isSelectionReady || !savedDay || isValidScheduleDate(savedDay)) return
    dateFieldRef.current?.scrollIntoView({ block: 'center' })
    dateFieldRef.current?.querySelector<HTMLElement>('[role="spinbutton"]')?.focus({ preventScroll: true })
  }, [isOpen, isScheduling, isSelectionReady, savedDate])

  // Keyed on the event, so a refetch of the same schedule while the dialog is open keeps the user's edits.
  const scheduleEventId = schedule?.eventId
  useEffect(() => {
    if (!isOpen || !schedule) return
    reset({ reasonId: schedule.reasonId ?? '', competitorId: schedule.competitorId ?? '', notes: schedule.notes ?? '' })
    setStartDates(schedule.startDates ?? DEFAULT_START_DATES)
    setTiming('SCHEDULED')
    setEffectiveDate(createDateFromString(schedule.effectiveDate))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, scheduleEventId, reset])

  // A competitor only means something under the reason it was picked for.
  const handleReasonChange = useCallback(() => {
    clearErrors('reasonId')
    // Not to the default: reopening a schedule makes its saved competitor the default.
    resetField('competitorId', { defaultValue: '' })
  }, [clearErrors, resetField])
  const handleCompetitorChange = useCallback(() => clearErrors('competitorId'), [clearErrors])
  const handleTimingChange = useCallback((value: ReactivationTiming) => {
    setEffectiveDateError(undefined)
    setTiming(value)
  }, [])
  const handleEffectiveDateChange = useCallback((date: Date | null) => {
    setEffectiveDateError(undefined)
    setEffectiveDate(date)
  }, [])

  const handleConfirm = useMemo(
    () =>
      handleSubmit(async values => {
        try {
          const { status } = await reactivateAccount({
            accountId,
            ...(isCaptureEnabled && values.reasonId && { wonReasonId: values.reasonId }),
            ...(isCaptureEnabled && showCompetitor && values.competitorId && { wonCompetitorId: values.competitorId }),
            ...(values.notes.trim() && { notes: values.notes.trim() }),
            locationIds: Array.from(selection.locationIds),
            configuredServiceIds: Array.from(selection.serviceIds),
            startDates,
            ...(scheduledDate && { effectiveDate: formatDateToBEFormatDateFns(scheduledDate) }),
          }).unwrap()
          // Work orders are created in the background for the restored services, and the inbox reports the batch.
          if (status === 'SCHEDULED') toast.success(T.REACTIVATION_SCHEDULED_FOR.replace('%s', formattedEffectiveDate ?? ''))
          else if (status === 'ALREADY_ACTIVE') toast.info(T.ACCOUNT_ALREADY_ACTIVE)
          else if (selection.serviceIds.size === 0) toast.success(T.ACCOUNT_REACTIVATED_SUCCESSFULLY)
          else if (canReceiveReactivationNotification) pushReactivationStartedToast(accountId)
          else toast.success(T.ACCOUNT_REACTIVATED_WORK_ORDERS_REGENERATING)
          onReactivated(status)
          onClose()
        } catch (error) {
          const { exceptionType, message } = (error as ServerError)?.data ?? {}
          if (exceptionType === 'wonReasonId') {
            setError('reasonId', { type: 'server', message })
            return
          }
          if (exceptionType === 'wonCompetitorId' && showCompetitor) {
            setError('competitorId', { type: 'server', message })
            return
          }
          if (exceptionType === 'effectiveDate' && scheduledDate) {
            setEffectiveDateError(message)
            return
          }
          handleErrorWithFetchError(error, T.ACCOUNT_REACTIVATION_FAILED)
        }
      }),
    [
      handleSubmit,
      reactivateAccount,
      accountId,
      isCaptureEnabled,
      showCompetitor,
      selection,
      startDates,
      scheduledDate,
      formattedEffectiveDate,
      canReceiveReactivationNotification,
      onReactivated,
      onClose,
      setError,
    ]
  )

  return (
    <HHBaseDialog fullWidth maxWidth="md" open={isOpen} onClose={onClose}>
      <HHDialogTitle title={T.REACTIVATE_ACCOUNT_DIALOG} onClose={onClose} />
      <DialogContent>
        <Stack gap={2.5} pt={1}>
          <HHAlert severity="info">
            {T.REACTIVATE_ACCOUNT_CAPTURE_LEAD}
            <strong>{accountName}</strong>
            {accountNumber ? ` (${accountNumber}).` : '.'}
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              <li>{T.REACTIVATION_NOTICE_SELECTION}</li>
              <li>{startDates === 'TODAY' ? todayBillingNotice : T.REACTIVATION_NOTICE_BILLING_KEEP_ORIGINAL}</li>
              <li>{T.REACTIVATION_NOTICE_UNROUTED}</li>
            </Box>
          </HHAlert>
          <ReactivationStartDates todayLabel={formattedEffectiveDate} value={startDates} onChange={setStartDates} />
          {hasSelectionError && <HHAlert severity="error">{T.REACTIVATION_SELECTION_LOAD_ERROR}</HHAlert>}
          {(isSelectionLoading || groups.length > 0) && (
            <Stack gap={1}>
              <Stack alignItems="center" direction="row" justifyContent="space-between">
                <Typography fontWeight={600} variant="body2">
                  {T.REACTIVATE_LOCATIONS_AND_SERVICES}
                </Typography>
                {!isSelectionLoading && (
                  <Button size="small" onClick={handleToggleAll}>
                    {hasSelection ? T.DESELECT_ALL : T.SELECT_ALL}
                  </Button>
                )}
              </Stack>
              {isSelectionLoading ? (
                <ReactivationSelectionListSkeleton />
              ) : (
                <>
                  <ReactivationSelectionList
                    groups={groups}
                    selection={selection}
                    onToggleLocation={handleToggleLocation}
                    onToggleService={handleToggleService}
                  />
                  <Typography color="text.secondary" variant="caption">
                    {selectionSummary}
                  </Typography>
                </>
              )}
            </Stack>
          )}
          <LifecycleCaptureFields
            competitorError={competitorError}
            competitorOptions={competitorOptions}
            control={control}
            isReasonCaptured={isCaptureEnabled}
            isReasonRequired={isReasonRequired}
            reasonError={reasonError}
            reasonHelperText={reasonHelperText}
            reasonLabel={T.WON_REASON}
            reasonOptions={reasonOptions}
            showCompetitor={showCompetitor}
            onCompetitorChange={handleCompetitorChange}
            onReasonChange={handleReasonChange}
          >
            <ReactivationWhen
              dateFieldRef={dateFieldRef}
              effectiveDate={effectiveDate}
              effectiveDateError={dateError}
              timing={timing}
              onEffectiveDateChange={handleEffectiveDateChange}
              onTimingChange={handleTimingChange}
            />
          </LifecycleCaptureFields>
        </Stack>
      </DialogContent>
      <HHDialogActions>
        <Button size="small" onClick={onClose}>
          {T.DISCARD}
        </Button>
        <Button
          color="success"
          disabled={!canConfirm || isSubmitting}
          loading={isLoading || isSubmitting}
          size="small"
          variant="contained"
          onClick={handleConfirm}
        >
          {isScheduling ? T.SCHEDULE_REACTIVATION : T.REACTIVATE_ACCOUNT_DIALOG}
        </Button>
      </HHDialogActions>
    </HHBaseDialog>
  )
}

export default ReactivateAccountDialog
